/**
 * Password strength helper
 * =============================================================================
 * Shared between the live PasswordStrengthIndicator component and any form
 * submission guard. Mirrors the rules enforced by the API on
 * POST /auth/change-password and POST /auth/reset-password.
 *
 * Keep these rules in sync with services/api/src/utils/passwordStrength.ts
 * if a server-side helper is introduced there.
 */

export const PASSWORD_MIN_LENGTH = 10;

export type PasswordRuleId =
  | 'minLength'
  | 'lowercase'
  | 'uppercase'
  | 'number'
  | 'special';

export interface PasswordRuleCheck {
  id: PasswordRuleId;
  passed: boolean;
}

export interface PasswordStrengthResult {
  /** Per-rule pass/fail. Drive the checklist UI from this. */
  checks: PasswordRuleCheck[];
  /** How many rules passed (0..5). */
  passedCount: number;
  /** Total number of rules (5). */
  totalCount: number;
  /**
   * Strength bucket 0..4 used to color the meter.
   * 0 = very weak, 4 = strong. See checkPasswordStrength for the mapping.
   */
  level: 0 | 1 | 2 | 3 | 4;
  /** True iff every rule passed. The submit button uses this. */
  meetsAllRequirements: boolean;
}

/**
 * Run all checks against the candidate password and grade it.
 * The function is pure - no side effects, fast enough to run on every keystroke.
 */
export function checkPasswordStrength(password: string): PasswordStrengthResult {
  const checks: PasswordRuleCheck[] = [
    { id: 'minLength', passed: password.length >= PASSWORD_MIN_LENGTH },
    { id: 'lowercase', passed: /[a-z]/.test(password) },
    { id: 'uppercase', passed: /[A-Z]/.test(password) },
    { id: 'number',    passed: /\d/.test(password) },
    { id: 'special',   passed: /[^A-Za-z0-9]/.test(password) },
  ];

  const passedCount = checks.filter((c) => c.passed).length;
  const totalCount = checks.length;

  // Base mapping: more rules met -> higher bucket.
  let level: 0 | 1 | 2 | 3 | 4 = 0;
  if (passedCount >= 5) level = 4;
  else if (passedCount === 4) level = 3;
  else if (passedCount === 3) level = 2;
  else if (passedCount === 2) level = 1;

  // Length bonus: a really long password is good even with weaker composition.
  // This keeps long passphrases like "correct horse battery staple" from being
  // graded "weak" just because they have no digits or symbols.
  if (password.length >= 16 && level < 3) level = 3;
  if (password.length >= 20 && level < 4) level = 4;

  return {
    checks,
    passedCount,
    totalCount,
    level,
    meetsAllRequirements: passedCount === totalCount,
  };
}
